import React, { useState, useEffect } from 'react'
import { getLoggingService, getLogSettings, type MessageActivityLog } from 'edge-stream-js'
import { SubscriberDetailedIcon } from '@edge-stream/app-pages-react'

// Wrapper component to add styling to the imported icon
function MailboxIcon() {
  return (
    <span style={{ verticalAlign: 'text-bottom', marginRight: '8px', display: 'inline-block' }}>
      <SubscriberDetailedIcon />
    </span>
  )
}

type ViewMode = 'live' | 'settings'

const statusColors: Record<string, string> = {
  delivered: '#27ae60',
  failed: '#e74c3c',
  pending: '#f39c12',
  error: '#e74c3c',
  queued: '#f39c12',
}

export interface SubscribersMonitorProps {
  /** Custom class name for the container */
  className?: string
  /** Custom styles for the container */
  style?: React.CSSProperties
  /** Enable/disable settings tab */
  showSettings?: boolean
  /** Polling interval in ms */
  pollInterval?: number
  /** Max logs to retrieve */
  maxLogs?: number
}

/**
 * SubscribersMonitor - Real-time monitoring of message delivery to subscribers
 *
 * Displays:
 * - Statistics on total deliveries, delivered, failed, and pending
 * - Topic breakdown showing message distribution across topics
 * - Real-time list of subscriber deliveries with filtering by topic
 * - Detailed delivery information including timing and errors
 * - Optional settings tab for enabling/disabling subscriber logging
 *
 * Usage:
 * ```tsx
 * import { SubscribersMonitor } from '@edge-stream/observability-react'
 *
 * <SubscribersMonitor
 *   showSettings={true}
 *   pollInterval={500}
 *   maxLogs={100}
 * />
 * ```
 */
export function SubscribersMonitor({
  className = 'page-container',
  style,
  showSettings = true,
  pollInterval = 500,
  maxLogs = 100,
}: SubscribersMonitorProps): React.ReactElement {
  const loggingService = getLoggingService()
  const logSettings = getLogSettings()

  const [viewMode, setViewMode] = useState<ViewMode>('live')
  const [logs, setLogs] = useState<MessageActivityLog[]>([])
  const [selectedLog, setSelectedLog] = useState<MessageActivityLog | null>(null)
  const [filter, setFilter] = useState<string>('')
  const [subscriberLoggingEnabled, setSubscriberLoggingEnabled] = useState(
    logSettings.isSubscriberLoggingEnabled()
  )

  // Refresh logs periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const allLogs = loggingService.getRecentLogs(maxLogs)
      setLogs(allLogs || [])
    }, pollInterval)
    return () => clearInterval(interval)
  }, [loggingService, pollInterval, maxLogs])

  const handleToggleSubscriberLogging = () => {
    const newState = !subscriberLoggingEnabled
    setSubscriberLoggingEnabled(newState)
    // Toggle subscriber logging in settings
  }

  // Extract all subscriber logs from message logs
  const allSubscriberLogs = logs.flatMap((msg) => msg.subscriberLogs || [])

  const filteredLogs = filter
    ? allSubscriberLogs.filter((log) => log.topic?.toLowerCase().includes(filter.toLowerCase()))
    : allSubscriberLogs

  const stats = {
    total: allSubscriberLogs.length,
    delivered: allSubscriberLogs.filter((l) => l.status === 'delivered').length,
    failed: allSubscriberLogs.filter((l) => l.status === 'error').length,
    pending: allSubscriberLogs.filter((l) => l.status === 'queued').length,
  }

  // Group by topic
  const topicStats = new Map<string, number>()
  allSubscriberLogs.forEach((log) => {
    const topic = log.topic || '(unknown)'
    topicStats.set(topic, (topicStats.get(topic) || 0) + 1)
  })

  return (
    <div className={className} style={style}>
      <div className="page-header" style={{ marginBottom: '2px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center' }}><MailboxIcon />Subscriber Delivery Monitor</h2>
        <p className="page-description">
          Real-time monitoring of message delivery to subscribers
        </p>
      </div>

      {/* Tabs */}
      {showSettings && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            paddingBottom: '10px',
            borderBottom: '1px solid var(--border)',
            marginBottom: '20px',
          }}
        >
          <button
            onClick={() => setViewMode('live')}
            style={{
              height: '36px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              background: viewMode === 'live' ? 'var(--color-brand, #427A43)' : 'transparent',
              color: viewMode === 'live' ? 'white' : 'var(--text-primary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Live Monitor
          </button>
          <button
            onClick={() => setViewMode('settings')}
            style={{
              height: '36px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              background: viewMode === 'settings' ? 'var(--color-brand, #427A43)' : 'transparent',
              color: viewMode === 'settings' ? 'white' : 'var(--text-primary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Settings
          </button>
        </div>
      )}

      {viewMode === 'live' && (
        <div>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Total Deliveries</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>{stats.total}</p>
            </div>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Delivered</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#27ae60' }}>{stats.delivered}</p>
            </div>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Failed</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#e74c3c' }}>{stats.failed}</p>
            </div>
            <div className="stat-card">
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Pending</p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#f39c12' }}>{stats.pending}</p>
            </div>
          </div>

          {/* Topic Stats */}
          {topicStats.size > 0 && (
            <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', borderRadius: '12px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#f8fafc' }}>Topics</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Array.from(topicStats.entries()).map(([topic, count]) => (
                  <div key={topic} style={{ padding: '4px 8px', background: 'var(--bg-tertiary)', borderRadius: '4px', fontSize: '12px' }}>
                    <code>{topic}</code> <strong>{count}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filter */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Filter by topic..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Delivery Logs */}
          <div
            style={
              filteredLogs.length === 0
                ? { background: 'rgba(15, 23, 42, 0.6)', border: '1px dashed rgba(58, 140, 69, 0.35)', borderRadius: '12px', overflow: 'hidden' }
                : { background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', borderRadius: '12px', overflow: 'hidden' }
            }
          >
            {filteredLogs.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
                No subscriber deliveries recorded. Messages are delivered to subscribers when received.
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {filteredLogs.map((log, idx) => {
                  // Extract group name from topic (e.g., "group:bas:Random" -> "Random" or "group.joined" -> "joined")
                  const topicParts = log.topic?.split(':') || [];
                  const groupName = topicParts.length > 2 ? topicParts[topicParts.length - 1] : (log.topic?.split('.')?.pop() || log.topic);

                  return (
                    <div
                      key={idx}
                      onClick={() => setSelectedLog(logs.find((m) => m.subscriberLogs?.includes(log)) || null)}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--border)',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                        cursor: 'pointer',
                        background: selectedLog?.subscriberLogs?.includes(log) ? 'var(--bg-secondary)' : 'transparent',
                        transition: 'background 0.2s',
                      }}
                    >
                      <div
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: statusColors[log.status] || '#999',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '80px' }}>
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '13px', minWidth: '120px', color: '#06b6d4' }}>
                        {groupName}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '150px' }}>
                        {log.topic}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1 }}>
                        {log.status}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail View */}
          {selectedLog && (
            <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc' }}>Message Delivery Summary</h4>
              <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.6 }}>
                <div><strong>Message ID:</strong> {selectedLog.messageId}</div>
                <div><strong>Topic:</strong> {selectedLog.topic}</div>
                <div><strong>Subscriber Deliveries:</strong> {selectedLog.subscriberLogs?.length || 0}</div>
                <div style={{ marginTop: '12px' }}>
                  <strong>Delivery Details:</strong>
                  {selectedLog.subscriberLogs?.map((s: any, idx: number) => (
                    <div key={idx} style={{ marginLeft: '0px', marginTop: '8px', padding: '12px', background: 'rgba(15, 23, 42, 0.6)', borderRadius: '8px', border: '1px solid rgba(58, 140, 69, 0.25)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: statusColors[s.status] || '#999',
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ fontWeight: 600, flex: 1 }}>{s.topic}</div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: '3px',
                          background: statusColors[s.status] || '#999',
                          color: 'white',
                          textTransform: 'uppercase'
                        }}>
                          {s.status}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', fontSize: '12px' }}>
                        {s.processingTime && (
                          <>
                            <div style={{ color: 'var(--text-secondary)' }}>Duration:</div>
                            <div>{s.processingTime}ms</div>
                          </>
                        )}
                        {s.error && (
                          <>
                            <div style={{ color: '#e74c3c' }}>Error:</div>
                            <div style={{ color: '#e74c3c' }}>{s.error?.message || 'Unknown error'}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showSettings && viewMode === 'settings' && (
        <div style={{ maxWidth: '500px' }}>
          <div className="activity-settings-card">
            <h4 style={{ margin: '0 0 16px 0', color: '#f8fafc' }}>Subscriber Logging Settings</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={subscriberLoggingEnabled} onChange={handleToggleSubscriberLogging} />
                <span>Enable subscriber logging</span>
              </label>
              <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                Subscriber logging tracks when messages are delivered to subscribers, including failures and retry attempts.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
